// src/pages/AboutPage.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./AboutPage.css";

import screen1 from "../assets/screen1.png";
import screen2 from "../assets/screen2.png";
import screen3 from "../assets/screen3.png";
import screen4 from "../assets/screen4.png";
import screen5 from "../assets/screen5.png";
import screen6 from "../assets/screen6.png";

// slideshow images
const screens = [screen1, screen2, screen3, screen4, screen5, screen6];

function AboutPage() {
  // --- slideshow state ---
  const [currentScreen, setCurrentScreen] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreen((prev) => (prev + 1) % screens.length);
    }, 2000); // 2 seconds

    return () => clearInterval(interval);
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://trendgenie.io/about",
    },
    headline: "About Trend Genie | Crypto Social, Mobile App, RugShield, Web3 Games",
    description:
      "Learn about Trend Genie, a crypto focused ecosystem building a social platform, Android app, RugShield security layer, and planned Web3 games. Explore our mission, products, and vision.",
    about: {
      "@type": "Organization",
      name: "Trend Genie",
      url: "https://trendgenie.io",
    },
    publisher: {
      "@type": "Organization",
      name: "Trend Genie",
      logo: {
        "@type": "ImageObject",
        url: "https://trendgenie.io/assets/logo.png",
      },
    },
  };

  return (
    <div className="page about-page">
      <Helmet>
        <title>About Trend Genie | AI Investment Analysis App</title>
        <meta
          name="description"
          content="Learn about Trend Genie, a crypto focused ecosystem building a social platform, Android app, RugShield security layer, and planned Web3 games. Explore our mission, products, and vision."
        />
        <script type="application/ld+json">
          {JSON.stringify(aboutSchema)}
        </script>
      </Helmet>

      {/* HERO – home-style two-column layout */}
      <section className="about-hero">
        <div className="about-hero-inner">
          {/* Left: text */}
          <div className="about-hero-left">
            <p className="about-hero-kicker">AI Powered</p>
            <h1>About Trend Genie</h1>
            <p className="about-hero-subtitle">
              Trend Genie is building a crypto focused ecosystem that combines practical tools and community. 
              Our Android app helps traders scan markets with AI driven structure, RugShield adds DeFi safety checks, 
              and Trend Genie Social brings crypto discussions, updates, and community insight into one place.
            </p>

            <div className="about-hero-cta-row">
              <a
                href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
                className="about-hero-btn"
                target="_blank"
                rel="noreferrer"
              >
                Explore the App
              </a>

              <div className="about-hero-badges">
                <span> Trend Genie Android App</span>
                <span> Trend Genie Social</span>
                <span> RugShield</span>
                <span> Web3 Games</span>
              </div>
            </div>
          </div>

          {/* Right: visual mockup */}
          <div className="about-hero-right">
            <div className="about-hero-tag">AI Powered</div>
            <div className="about-hero-device">
              <div className="about-hero-device-header" />
              <div className="about-hero-device-chart">
                {/* Slideshow image inside phone */}
                <img
                  src={screens[currentScreen]}
                  alt="Trend Genie app screen"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // 'contain' if you want full UI visible
                    borderRadius: "20px",
                    display: "block",
                    position: "relative",
                    zIndex: 2,
                    transition: "opacity 0.4s ease-in-out",
                  }}
                />
              </div>
              <div className="about-hero-device-footer">
                <span className="about-hero-device-label">Trend Genie view</span>
                <span className="about-hero-device-pill">Real App Preview</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      

      {/* WHAT / WHY / HOW GRID */}
      <section className="about-grid">
        <div className="about-grid-item">
          <h2>What Trend Genie Does</h2>
          <p>
            Trend Genie scans markets so you do not have to watch every candle.
            The app highlights potential trade setups, trends, and momentum
            shifts for stocks and crypto, directly on your Android phone.
          </p>
          <p>
            Instead of juggling dozens of indicators, Trend Genie focuses on
            signal quality. The goal is fewer, clearer ideas that are easier to
            execute with discipline.
          </p>
        </div>

        <div className="about-grid-item">
          <h2>Why We Built It</h2>
          <p>
            Most traders are not full time professionals. They trade around
            work, studies, and family. They want access to smart tools, but they
            do not want black box systems or promises that sound unrealistic.
          </p>
          <p>
            Trend Genie was created for this reality. It is designed to reduce
            screen time, cut down on noise, and give traders a structured way to
            act on what matters instead of reacting to every move.
          </p>
        </div>

        <div className="about-grid-item">
          <h2>How It Helps You Trade Smarter</h2>
          <p>
            With Trend Genie you can let AI handle market scanning while you
            stay focused on risk, routines, and your personal rules. Signals are
            delivered in a clear format so you can quickly see direction,
            context, and where you might build a plan around the idea.
          </p>
          <p>
            You stay in control of risk per trade, position size, and exits. The
            app supports your process instead of replacing it.
          </p>
        </div>
      </section>

      {/* PRODUCT PRINCIPLES */}
      <section className="about-section about-principles-section">
        <h2>Our Product Principles</h2>
        <div className="about-principles">
          <div className="about-principle">
            <h3>Clarity over complexity</h3>
            <p>
              Trading already has enough complexity. Trend Genie avoids
              cluttered interfaces, overloaded charts, and confusing parameters.
              You see only what you need to make a decision.
            </p>
          </div>
          <div className="about-principle">
            <h3>Data over hype</h3>
            <p>
              Social media noise and random opinions can push traders into bad
              decisions. Trend Genie focuses on data, structure, and repeatable
              logic. Ideas are generated from market behavior, not headlines.
            </p>
          </div>
          <div className="about-principle">
            <h3>Control stays with the trader</h3>
            <p>
              Trend Genie does not place trades for you or hide what is
              happening. Signals are suggestions that you can accept, refine, or
              ignore. You always keep control of execution and risk.
            </p>
          </div>
          <div className="about-principle">
            <h3>Mobile first, serious mindset</h3>
            <p>
              Trading from a phone should not feel like a game. The app is built
              for mobile convenience but anchored in serious concepts such as
              risk limits, journaling, and long term consistency.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR – HOME-STYLE CARDS */}
      <section className="about-section">
        <h2>Who Trend Genie Is For</h2>
        <div className="about-columns">
          <div className="about-column">
            <h3>Part time traders</h3>
            <p>
              You trade around a job or degree and cannot sit in front of a
              screen all day. You want high quality signals on your phone, not
              endless scanning.
            </p>
          </div>
          <div className="about-column">
            <h3>System driven thinkers</h3>
            <p>
              You care about rules, risk, and long term edge. You want AI to
              help filter markets but you refuse to hand over full control to an
              opaque system.
            </p>
          </div>
          <div className="about-column">
            <h3>New traders willing to learn</h3>
            <p>
              You are still building experience and want guidance that
              encourages discipline instead of shortcuts. Trend Genie articles
              and in app education are designed to support that path.
            </p>
          </div>
        </div>
      </section>

      {/* ✅ TREND GENIE SOCIAL (CARD STYLE) */}
<section className="about-section">
  <h2>Trend Genie Social</h2>
  <h3>Crypto Community Platform</h3>
  <p>
    Trend Genie Social is the community layer of the Trend Genie ecosystem. It is built for crypto users who want a focused place to share updates,
    discuss market narratives, and follow what the community is watching, without the noise of general social platforms.
  </p>

  <div className="about-columns">
    <div className="about-column">
      <h3>Social feed built for crypto</h3>
      <p>
        Publish posts, share insights, and engage through reactions and comments in a clean, easy to use feed designed for crypto focused conversation.
      </p>
    </div>

    <div className="about-column">
      <h3>Discover trends and community topics</h3>
      <p>
        Explore what is gaining attention inside the community, follow discussions, and stay aligned with the latest themes people are talking about.
      </p>
    </div>

    <div className="about-column">
      <h3>Profiles and community identity</h3>
      <p>
        Build a public presence, connect with like minded users, and be part of a community centered on Web3, DeFi, and the Trend Genie ecosystem.
      </p>
    </div>
  </div>
</section>

{/* ✅ TREND GENIE SOCIAL CTA */}
<section className="about-section about-cta">
  <h2>Join Trend Genie Social</h2>
  <p>
    Connect with the Trend Genie crypto community, share updates, and follow discussions that matter.
  </p>
  <a
    href="https://social.trendgenie.io"
    target="_blank"
    rel="noreferrer"
    className="about-cta-btn"
  >
    Open Trend Genie Social
  </a>
</section>


       {/* VISION */}
      <section className="about-section">
        <h2>Our Vision For Trend Genie</h2>

        <div className="about-columns">
          <div className="about-column">
            <h3>Practical AI trading companion</h3>
            <p>
              Trend Genie aims to be the most practical AI trading companion on
              Android. The focus is on clear, actionable insights instead of
              hype, so part-time traders can scan markets quickly and still stay
              in control of every decision.
            </p>
          </div>

          <div className="about-column">
            <h3>Deeper insight into market behaviour</h3>
            <p>
              Our roadmap includes better visual explanations, more context
              around price action, and tools that help you understand how
              different markets behave over time across crypto and, later,
              other asset classes.
            </p>
          </div>

          <div className="about-column">
            <h3>Tools that support discipline</h3>
            <p>
              Over time, Trend Genie will add more education, habit tracking,
              and analytics on your own trading so you can link your strategy
              and risk profile with the signals you see inside the app and build
              a more consistent process.
            </p>
          </div>
        </div>
         <div className="about-columns">
          <div className="about-column">
            <h3>RugShield Defi Protector</h3>
            <p>
              RugShield is Trend Genie’s DeFi safety scanner for ERC-20 and BEP-20 tokens, 
              built to help everyday traders avoid obvious scams before they ape into a project. 
              Paste a contract address and get an instant, easy-to-read risk snapshot based on real on-chain data.
            </p>
          </div>

          <div className="about-column">
            <h3>$TGEN Utility Token</h3>
            <p>
            $TGEN is the native BEP-20 utility token of the Trend Genie ecosystem on BNB Smart Chain. 
            It connects the AI trading app, RugShield scanner, and future web tools into one experience, 
            focusing on access, usage tiers, and long-term alignment with real platform users.
            </p>
          </div>

          <div className="about-column">
            <h3>Future Plans & Roadmap</h3>
            <p>
              Trend Genie’s roadmap is about practical trading tools and DeFi security products that 
              normal people can actually use. We’re deepening our AI engine, adding more exchanges and 
              pairs, and expanding RugShield checks and chain support so you can scan charts, scan contracts, 
              and manage risk in one place.
            </p>
          </div>
        </div>
      </section>

      {/* ✅ RUGSHIELD – DEFI SAFETY SCANNER (CARD STYLE) */}
      <section className="about-section">
        <h2>Trend Genie RugShield – DeFi Safety Scanner</h2>
        <p>
          RugShield is Trend Genie&apos;s DeFi safety scanner for ERC-20 and
          BEP-20 tokens. Paste a contract address and get an instant risk read
          before you ape into a token.
        </p>

        <div className="about-columns">
          <div className="about-column">
            <h3>Scan any token contract</h3>
            <p>
              RugShield scans token contracts in seconds and highlights key DeFi
              risk factors such as honeypot behaviour, ownership concentration,
              liquidity health, and trading taxes so you can quickly decide
              whether a token deserves more research.
            </p>
          </div>
          <div className="about-column">
            <h3>Spot common rug-pull patterns</h3>
            <p>
              Many scams repeat the same structure. RugShield helps you catch
              familiar rug-pull patterns – unlocked liquidity, dangerous
              minting powers, blacklist/whitelist logic, or wallets that control
              too much supply – before you get trapped in a trade you cannot
              exit.
            </p>
          </div>
          <div className="about-column">
            <h3>Beginner & pro friendly</h3>
            <p>
              RugShield is built for everyday traders, students, and newcomers,
              not just Solidity devs. You get a clear traffic-light style risk
              summary with the option to switch between simple explanations and
              more technical detail when you want to go deeper.
            </p>
          </div>
        </div>
      </section>
      {/* ✅ RUGSHIELD CTA */}
      <section className="about-section about-cta">
        <h2>Try RugShield Before You Ape In</h2>
        <p>
          Paste any ERC-20 or BEP-20 contract into RugShield and get an instant
          risk snapshot powered by on chain data right inside the Trend Genie
          web app.
        </p>
        <a
          href="https://app.trendgenie.io"
          target="_blank"
          rel="noreferrer"
          className="about-cta-btn"
        >
          Open RugShield on Web
        </a>
      </section>

      {/* ✅ $TGEN UTILITY TOKEN (CARD STYLE) */}
      <section className="about-section">
        <h2>$TGEN Utility Token – Powering Trend Genie</h2>
        <p>
          $TGEN is Trend Genie&apos;s BEP-20 utility token on BNB Smart Chain.
          It connects our tools into one ecosystem and rewards traders who
          actively use Trend Genie and RugShield instead of only speculating on
          price.
        </p>

        <div className="about-columns">
          <div className="about-column">
            <h3>Access tiers & usage limits</h3>
            <p>
              Holding TGEN can unlock higher RugShield scan limits, richer AI
              explanations in the app, and extra quality-of-life features. As
              your balance grows, you move into tiers designed for active users
              who lean on Trend Genie every day.
            </p>
          </div>
          <div className="about-column">
            <h3>Built for real users</h3>
            <p>
              TGEN is aimed at traders, students, and builders who care about
              safer DeFi and better decision-making. Long term, holders will
              be able to test new modules early and give feedback on which
              chains, metrics, and education flows we ship next.
            </p>
          </div>
          <div className="about-column">
            <h3>Clear, transparent token design</h3>
            <p>
              TGEN is a fixed-supply utility token, not equity and not a
              guarantee of profit. Our{" "}
              <a href="/whitepaper" target="_self" rel="noreferrer">
                whitepaper
              </a>{" "}
              explains how token utility, RugShield and the Trend Genie app fit
              together inside one long-term roadmap on BNB Smart Chain.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-section about-cta">
        <h2>Start Trading With More Structure</h2>
        <p>
          Download Trend Genie on your Android device and turn AI powered market
          scanning into a simple, repeatable part of your trading routine.
        </p>
        <a
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
          rel="noreferrer"
          className="about-cta-btn"
        >
          Get Trend Genie on Android
        </a>
      </section>
    </div>
  );
}

export default AboutPage;
